import moment from "moment";
import { API_DATE_FORMAT } from "src/consts/global";
import {
  PROJECT_ORG_STRUCTURE_PIT_OPTIONS,
  PROJECT_PREFILL_OPTIONS,
} from "src/allocation/consts";
import { UserMessageError } from "src/util/error";
import { readStoredReferenceDate } from "src/allocation/util/referenceDateSelection";
import { PLANNER } from "./pageModes";
import { getPersistedExpandedIds } from "../expandedIds.util";
import mainQuery from "./mainQueryReducer/initialState";
import model from "./modelReducer/initialState";
import lookupData from "./lookupDataReducer/initialState";
import userInteraction from "./userInteractionReducer/initialState";

export default ({
  group,
  workspace,
  viewMode,
  pageMode,
  targetGroupSearchParamId,
  sourceGroupSearchParamId,
  hideHiddenTeams,
  showObjectives,
  withPerspective,
  activeAllocationProject,
}) => {
  // default reference date and prefill to today until we figure them out
  const prefillOption =
    activeAllocationProject?.prefillOption ?? PROJECT_PREFILL_OPTIONS.TODAY;

  let prefillMoment;
  switch (prefillOption) {
    case "START_DATE": {
      prefillMoment = moment.utc(activeAllocationProject.startDate);
      break;
    }
    case "EFFECTIVE_DATE": {
      prefillMoment = moment.utc(activeAllocationProject.allocationDate);
      break;
    }
    case "CUSTOM_DATE": {
      if (activeAllocationProject.baselineDate) {
        prefillMoment = moment.utc(activeAllocationProject.baselineDate);
      } else {
        // A guard against a user not setting a baselineDate yet.
        // This is a workaround for now as any bad project setting should error with a notification to the user
        // to setup planner with correct settings
        prefillMoment = moment();
      }
      break;
    }
    case "TODAY":
    default: {
      // today
      prefillMoment = moment();
    }
  }

  if (!prefillMoment.isValid()) {
    const workspaceId = workspace?.id;
    const actionLink = workspaceId
      ? {
          label: "Go to Datasources",
          path: `/${workspaceId}/configuration/datasources`,
        }
      : undefined;

    throw new UserMessageError(
      "The prefill date configuration is invalid. Please review and update the prefill date settings in your allocation project datasource.",
      { prefillOption, activeAllocationProject },
      actionLink,
      {
        title: "Invalid Configuration",
        icon: ["far", "cog"],
        iconColor: "primary",
      }
    );
  }

  const prefillDate = prefillMoment.format(API_DATE_FORMAT);

  // The reference date is a planner concept and planner is the only page with
  // UI for it. Forecast reading the persisted value meant a date chosen in
  // planner (or team builder) silently became forecast's actuals anchor, with
  // no way for a forecast user to see or reset it (MS-1040).
  const savedReferenceDate =
    pageMode === PLANNER
      ? readStoredReferenceDate(activeAllocationProject)
      : null;
  const referenceDate = savedReferenceDate || prefillDate;

  const orgStructurePITOption =
    activeAllocationProject?.orgStructurePITOption ??
    PROJECT_ORG_STRUCTURE_PIT_OPTIONS.TODAY;

  let orgStructurePITDate;
  if (orgStructurePITOption) {
    switch (orgStructurePITOption) {
      case PROJECT_ORG_STRUCTURE_PIT_OPTIONS.TODAY: {
        orgStructurePITDate = moment().format(API_DATE_FORMAT);
        break;
      }
      case PROJECT_ORG_STRUCTURE_PIT_OPTIONS.PREFILL_DATE: {
        orgStructurePITDate = prefillDate;
        break;
      }
      default: {
        // today
        orgStructurePITDate = moment().format(API_DATE_FORMAT);
        break;
      }
    }
  }

  const { previouslyExpandedGroupIds, previouslyExpandedLineItemsGroupIds } =
    getPersistedExpandedIds(group, pageMode, viewMode);

  return {
    pageMode,
    activeAllocationProject: {
      ...activeAllocationProject,
      prefillOption,
      orgStructurePITOption,
    },
    previouslyExpandedGroupIds,
    mainQuery: mainQuery({
      group,
      workspace,
      pageMode,
      queryWithPerspective: withPerspective,
      viewMode,
      activeAllocationProject,
      referenceDate,
      prefillDate,
      prefillOption,
      orgStructurePITOption,
      orgStructurePITDate,
      hideHiddenTeams,
    }),
    model: model({
      group,
      workspace,
      pageMode,
      viewMode,
      hideHiddenTeams,
      showObjectives,
      activeAllocationProject,
    }),
    lookupData: lookupData(group, workspace),
    userInteraction: userInteraction(
      viewMode,
      targetGroupSearchParamId,
      sourceGroupSearchParamId,
      previouslyExpandedGroupIds,
      previouslyExpandedLineItemsGroupIds
    ),
  };
};
